import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { 
  AppSettings, 
  SettingsUpdateRequest,
  DataSharingRequest,
  DataSharingResponse,
  DataSourceUpdateRequest,
  DataSourceResponse,
  IntegrationStatusResponse
} from '../../../types/settings';

export const settingsApi = {
  // Récupérer tous les paramètres
  getSettings: (): Promise<ApiResponse<AppSettings>> =>
    ApiService.get<AppSettings>('/settings'),

  // Mettre à jour une catégorie de paramètres
  updateSettings: (request: SettingsUpdateRequest): Promise<ApiResponse<AppSettings>> =>
    ApiService.put<AppSettings>(`/settings/${request.category}`, request.settings),

  // Récupérer une catégorie spécifique
  getSettingsCategory: (category: keyof AppSettings): Promise<ApiResponse<AppSettings[keyof AppSettings]>> =>
    ApiService.get<AppSettings[keyof AppSettings]>(`/settings/${category}`),

  // Réinitialiser les paramètres par défaut
  resetSettings: (category?: keyof AppSettings): Promise<ApiResponse<AppSettings>> => {
    const url = category ? `/settings/${category}/reset` : '/settings/reset';
    return ApiService.post<AppSettings>(url, {});
  },

  // Importer des paramètres
  importSettings: (settings: Partial<AppSettings>): Promise<ApiResponse<AppSettings>> =>
    ApiService.post<AppSettings>('/settings/import', settings),

  // Exporter les paramètres
  exportSettings: (): Promise<ApiResponse<AppSettings>> =>
    ApiService.get<AppSettings>('/settings/export'),

  // Valider les paramètres
  validateSettings: (settings: Partial<AppSettings>): Promise<ApiResponse<{ valid: boolean; errors?: string[] }>> =>
    ApiService.post<{ valid: boolean; errors?: string[] }>('/settings/validate', settings),

  // === NOUVEAUX ENDPOINTS POUR LES INTÉGRATIONS AVANCÉES ===
  
  // Partage de données
  getDataSharingSettings: (): Promise<ApiResponse<DataSharingResponse['data']>> =>
    ApiService.get<DataSharingResponse['data']>('/settings/data-sharing'),

  updateDataSharingSettings: (request: DataSharingRequest): Promise<ApiResponse<DataSharingResponse['data']>> =>
    ApiService.put<DataSharingResponse['data']>('/settings/data-sharing', request),

  // Sources de données
  getDataSourceSettings: (): Promise<ApiResponse<DataSourceResponse['data']>> =>
    ApiService.get<DataSourceResponse['data']>('/settings/data-sources'),

  updateDataSourceSettings: (request: DataSourceUpdateRequest): Promise<ApiResponse<DataSourceResponse['data']>> =>
    ApiService.put<DataSourceResponse['data']>('/settings/data-sources', request),

  // Statut des intégrations
  getIntegrationStatus: (): Promise<ApiResponse<IntegrationStatusResponse['data']>> =>
    ApiService.get<IntegrationStatusResponse['data']>('/settings/integrations/status'),

  // Configuration bancaire
  getBankIntegrations: (): Promise<ApiResponse<any>> =>
    ApiService.get<any>('/settings/integrations/banks'),

  updateBankIntegration: (bankId: string, config: any): Promise<ApiResponse<any>> =>
    ApiService.put<any>(`/settings/integrations/banks/${bankId}`, config),

  // Configuration e-invoicing
  getEInvoiceSettings: (): Promise<ApiResponse<any>> =>
    ApiService.get<any>('/settings/integrations/e-invoice'),

  updateEInvoiceSettings: (config: any): Promise<ApiResponse<any>> =>
    ApiService.put<any>('/settings/integrations/e-invoice', config),

  // Configuration fiscale
  getTaxIntegrationSettings: (): Promise<ApiResponse<any>> =>
    ApiService.get<any>('/settings/integrations/tax'),

  updateTaxIntegrationSettings: (config: any): Promise<ApiResponse<any>> =>
    ApiService.put<any>('/settings/integrations/tax', config),

  // Configuration portfolio
  getPortfolioIntegrationSettings: (): Promise<ApiResponse<any>> =>
    ApiService.get<any>('/settings/integrations/portfolio'),

  updatePortfolioIntegrationSettings: (config: any): Promise<ApiResponse<any>> =>
    ApiService.put<any>('/settings/integrations/portfolio', config)
};
